'use client';

import React, { forwardRef } from 'react';
import { Shield, CheckCircle } from 'lucide-react';
import { useTheme } from '@/core/providers/ThemeProvider';

interface CampaignStatusProps {
  campaignData: {
    status: string;
    brand: string;
    useCase: string;
    approvedDate: string;
    throughput: number;
  };
  compact?: boolean;
}

const CampaignStatus = forwardRef<HTMLDivElement, CampaignStatusProps>(({ campaignData, compact = false }, ref) => {
  const { theme } = useTheme();

  if (compact) {
    return (
      <div ref={ref} className="mb-6">
        <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Shield className="h-5 w-5 mr-2" style={{ color: theme.textSecondary }} />
          <h2 className="text-lg font-medium" style={{ color: theme.textPrimary }}>Campaign Registration</h2>
        </div>
        <div
          className={`px-3 py-1 text-sm font-medium rounded-full ${
            campaignData.status === 'approved'
              ? 'text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30'
              : 'text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30'
          }`}
        >
          {campaignData.status === 'approved' ? 'Approved' : 'Pending Review'}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div className="p-3 rounded-lg bg-hover-background">
          <div className="text-xs uppercase tracking-wider mb-1" style={{ color: theme.textSecondary }}>Brand</div>
          <div className="text-sm font-medium truncate" style={{ color: theme.textPrimary }}>{campaignData.brand}</div>
        </div>
        <div className="p-3 rounded-lg bg-hover-background">
          <div className="text-xs uppercase tracking-wider mb-1" style={{ color: theme.textSecondary }}>Use Case</div>
          <div className="text-sm font-medium truncate" style={{ color: theme.textPrimary }}>{campaignData.useCase}</div>
        </div>
        <div className="p-3 rounded-lg bg-hover-background">
          <div className="text-xs uppercase tracking-wider mb-1" style={{ color: theme.textSecondary }}>Since</div>
          <div className="text-sm font-medium" style={{ color: theme.textPrimary }}>
            {campaignData.approvedDate ? new Date(campaignData.approvedDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'N/A'}
          </div>
        </div>
        <div className="p-3 rounded-lg bg-hover-background">
          <div className="text-xs uppercase tracking-wider mb-1" style={{ color: theme.textSecondary }}>Throughput</div>
          <div className="text-sm font-medium" style={{ color: theme.textPrimary }}>{campaignData.throughput} msg/s</div>
        </div>
      </div>

      <div className="flex items-center p-3 rounded-lg" style={{
        backgroundColor: campaignData.status === 'approved' ? `${theme.primary}08` : '#FEF3C708',
        borderLeft: `3px solid ${campaignData.status === 'approved' ? theme.primary : '#F59E0B'}`
      }}>
        <CheckCircle
          className="h-4 w-4 mr-2 flex-shrink-0"
          style={{ color: campaignData.status === 'approved' ? theme.primary : '#F59E0B' }}
        />
        <div className="text-xs" style={{ color: theme.textSecondary }}>
          {campaignData.status === 'approved'
            ? 'Your campaign is fully approved and ready for high-volume messaging'
            : 'Campaign under review. You can still send messages at standard rates while pending approval'
          }
        </div>
      </div>
      </div>
    );
  }

  return (
    <div ref={ref} className="bg-card-background rounded-lg border p-6" style={{ borderColor: theme.border }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Shield className="h-5 w-5 mr-2" style={{ color: theme.textSecondary }} />
          <h2 className="text-lg font-medium" style={{ color: theme.textPrimary }}>Campaign Registration</h2>
        </div>
        <div
          className={`px-3 py-1 text-sm font-medium rounded-full ${
            campaignData.status === 'approved'
              ? 'text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30'
              : 'text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30'
          }`}
        >
          {campaignData.status === 'approved' ? 'Approved' : 'Pending Review'}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div className="p-3 rounded-lg bg-hover-background">
          <div className="text-xs uppercase tracking-wider mb-1" style={{ color: theme.textSecondary }}>Brand</div>
          <div className="text-sm font-medium truncate" style={{ color: theme.textPrimary }}>{campaignData.brand}</div>
        </div>
        <div className="p-3 rounded-lg bg-hover-background">
          <div className="text-xs uppercase tracking-wider mb-1" style={{ color: theme.textSecondary }}>Use Case</div>
          <div className="text-sm font-medium truncate" style={{ color: theme.textPrimary }}>{campaignData.useCase}</div>
        </div>
        <div className="p-3 rounded-lg bg-hover-background">
          <div className="text-xs uppercase tracking-wider mb-1" style={{ color: theme.textSecondary }}>Since</div>
          <div className="text-sm font-medium" style={{ color: theme.textPrimary }}>
            {campaignData.approvedDate ? new Date(campaignData.approvedDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'N/A'}
          </div>
        </div>
        <div className="p-3 rounded-lg bg-hover-background">
          <div className="text-xs uppercase tracking-wider mb-1" style={{ color: theme.textSecondary }}>Throughput</div>
          <div className="text-sm font-medium" style={{ color: theme.textPrimary }}>{campaignData.throughput} msg/s</div>
        </div>
      </div>

      <div className="flex items-center p-3 rounded-lg" style={{
        backgroundColor: campaignData.status === 'approved' ? `${theme.primary}08` : '#FEF3C708',
        borderLeft: `3px solid ${campaignData.status === 'approved' ? theme.primary : '#F59E0B'}`
      }}>
        <CheckCircle
          className="h-4 w-4 mr-2 flex-shrink-0"
          style={{ color: campaignData.status === 'approved' ? theme.primary : '#F59E0B' }}
        />
        <div className="text-xs" style={{ color: theme.textSecondary }}>
          {campaignData.status === 'approved'
            ? 'Your campaign is fully approved and ready for high-volume messaging'
            : 'Campaign under review. You can still send messages at standard rates while pending approval'
          }
        </div>
      </div>
    </div>
  );
});

CampaignStatus.displayName = 'CampaignStatus';

export default CampaignStatus; 